import {useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';

import {DEFAULT_EMPTY_CELL_VALUE_MODE} from '../../../../constants/defaultEmptyCellValueMode';
import {DEFAULT_PAGINATION_DISPLAY_MODE} from '../../../../constants/defaultPaginationDisplayMode';
import {DEFAULT_TRUNCATED_CELL_HOVER_MODE} from '../../../../constants/defaultTruncatedCellHoverMode';
import {PAGE_SIZE_OPTIONS as PAGE_SIZE_VALUES} from '../../../../constants/pageSizeOptions';
import {PAGINATION_DISPLAY_MODE_FEATURE_FLAG} from '../../../../constants/paginationDisplayModeFeatureFlag';
import type {
  EmptyCellValueMode,
  FeGridSettings,
  PaginationDisplayMode,
  PresetDisplayMode,
  TruncatedCellHoverMode,
} from '../../../../types/Api';
import type {LineHeight} from '../../../../utils/lineHeightToValue';
import {SegmentedControl, type SegmentOption} from './components/SegmentedControl/SegmentedControl';
import {SettingGroup} from './components/SettingGroup/SettingGroup';
import {SettingToggle} from './components/SettingToggle/SettingToggle';

export type SettingsPanelProps = {
  settings: FeGridSettings;
  columnSortMode?: 'SINGLE_SORT' | 'MULTI_SORT' | 'NONE';
  onSettingsChange: (settings: Partial<FeGridSettings>) => void;
  /** Current preset display mode — only shown when presets are enabled. */
  presetDisplayMode?: PresetDisplayMode;
  /** Callback to change the preset display mode. */
  onPresetDisplayModeChange?: (mode: PresetDisplayMode) => void;
  /** Controls whether the always-visible summary row toggle is enabled. */
  summaryRowEnabled?: boolean;
};

/** Option whose label is a translation key resolved at render time (+ en fallback). */
type TranslatableOption<T> = {value: T; labelKey: string; defaultLabel: string};

type Translate = ReturnType<typeof useTranslationContext>;

const translateOptions = <T extends string | number>(
  options: TranslatableOption<T>[],
  t: Translate
): SegmentOption<T>[] =>
  options.map((option) => ({
    value: option.value,
    label: t(option.labelKey, {defaultValue: option.defaultLabel}),
  }));

const ROW_HEIGHT_OPTIONS: TranslatableOption<LineHeight>[] = [
  {value: 'COMPACT', labelKey: 'page.datagrid.labels.rowHeightCompact', defaultLabel: 'Compact'},
  {value: 'SHORT', labelKey: 'page.datagrid.labels.rowHeightSmall', defaultLabel: 'Small'},
  {value: 'DEFAULT', labelKey: 'page.datagrid.labels.rowHeightDefault', defaultLabel: 'Default'},
  {value: 'TALL', labelKey: 'page.datagrid.labels.rowHeightTall', defaultLabel: 'Tall'},
  {
    value: 'THUMBNAIL',
    labelKey: 'page.datagrid.labels.rowHeightLargeThumbnail',
    defaultLabel: 'Large thumbnails',
  },
];

const HEADER_HEIGHT_OPTIONS: TranslatableOption<LineHeight>[] = ROW_HEIGHT_OPTIONS;

const PAGE_SIZE_OPTIONS: SegmentOption<number>[] = PAGE_SIZE_VALUES.map((n) => ({
  value: n,
  label: String(n),
}));

const WRAP_MODE_OPTIONS: TranslatableOption<'TRUNCATE' | 'WRAP'>[] = [
  {
    value: 'TRUNCATE',
    labelKey: 'page.datagrid.labels.valueWrappingTruncate',
    defaultLabel: 'Truncate',
  },
  {value: 'WRAP', labelKey: 'page.datagrid.labels.valueWrappingWrap', defaultLabel: 'Wrap'},
];

const TRUNCATED_CELL_HOVER_MODE_OPTIONS: TranslatableOption<TruncatedCellHoverMode>[] = [
  {value: 'TOOLTIP', labelKey: 'page.datagrid.settings.hoverTooltip', defaultLabel: 'Tooltip'},
  {value: 'EXPAND', labelKey: 'page.datagrid.settings.hoverExpand', defaultLabel: 'Expand'},
  {value: 'NONE', labelKey: 'page.datagrid.labels.none', defaultLabel: 'None'},
];

const EMPTY_CELL_VALUE_MODE_OPTIONS: TranslatableOption<EmptyCellValueMode>[] = [
  {
    value: 'NOTHING',
    labelKey: 'page.datagrid.settings.emptyCellsNothing',
    defaultLabel: 'Nothing',
  },
  {value: 'DASH', labelKey: 'page.datagrid.settings.emptyCellsDash', defaultLabel: 'Dash'},
];

const PAGINATION_DISPLAY_MODE_OPTIONS: TranslatableOption<PaginationDisplayMode>[] = [
  {
    value: 'BUTTONS',
    labelKey: 'page.datagrid.settings.paginationDisplayButtons',
    defaultLabel: 'Pages',
  },
  {
    value: 'INPUT',
    labelKey: 'page.datagrid.settings.paginationDisplayInput',
    defaultLabel: 'Input',
  },
];

const PRESET_DISPLAY_MODE_OPTIONS: TranslatableOption<PresetDisplayMode>[] = [
  {
    value: 'TABS_WRAP',
    labelKey: 'page.datagrid.settings.presetDisplayTabs',
    defaultLabel: 'Tabs',
  },
  {
    value: 'TABS_SCROLL',
    labelKey: 'page.datagrid.settings.presetDisplayScroll',
    defaultLabel: 'Scroll',
  },
  {
    value: 'DROPDOWN',
    labelKey: 'page.datagrid.settings.presetDisplayDropdown',
    defaultLabel: 'Dropdown',
  },
  {
    value: 'NONE',
    labelKey: 'page.datagrid.settings.presetDisplayHidden',
    defaultLabel: 'Hidden',
  },
];

export function SettingsPanel(props: SettingsPanelProps) {
  const t = useTranslationContext();

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="overflow-y-auto flex-1" style={{padding: 0}}>
        <SettingGroup title={t('page.datagrid.settings.density', {defaultValue: 'Density'})}>
          <SegmentedControl
            label={t('page.datagrid.labels.headerHeight', {defaultValue: 'Header height'})}
            options={translateOptions(HEADER_HEIGHT_OPTIONS, t)}
            value={props.settings.headerHeight}
            onChange={(value) => props.onSettingsChange({headerHeight: value})}
          />
          <SegmentedControl
            label={t('page.datagrid.labels.rowHeight', {defaultValue: 'Row height'})}
            options={translateOptions(ROW_HEIGHT_OPTIONS, t)}
            value={props.settings.rowHeight}
            onChange={(value) => props.onSettingsChange({rowHeight: value})}
          />
          <SegmentedControl
            label={t('page.datagrid.labels.valueWrapping', {defaultValue: 'Value wrapping'})}
            options={translateOptions(WRAP_MODE_OPTIONS, t)}
            value={props.settings.valueWrapMode}
            onChange={(value) => props.onSettingsChange({valueWrapMode: value})}
            hint={t('page.datagrid.settings.valueWrappingHint', {
              defaultValue:
                'Wrap shows full text on multiple lines; truncate keeps rows one line tall.',
            })}
          />
          <SegmentedControl
            label={t('page.datagrid.settings.truncatedCellHover', {
              defaultValue: 'Truncated cell hover',
            })}
            options={translateOptions(TRUNCATED_CELL_HOVER_MODE_OPTIONS, t)}
            value={props.settings.truncatedCellHoverMode ?? DEFAULT_TRUNCATED_CELL_HOVER_MODE}
            onChange={(value) => props.onSettingsChange({truncatedCellHoverMode: value})}
            hint={t('page.datagrid.settings.truncatedCellHoverHint', {
              defaultValue:
                'What reveals the full text of a truncated cell on hover: a tooltip, the cell expanding in place, or nothing.',
            })}
          />
          <SegmentedControl
            label={t('page.datagrid.settings.emptyCells', {defaultValue: 'Empty cells'})}
            options={translateOptions(EMPTY_CELL_VALUE_MODE_OPTIONS, t)}
            value={props.settings.emptyCellValueMode ?? DEFAULT_EMPTY_CELL_VALUE_MODE}
            onChange={(value) => props.onSettingsChange({emptyCellValueMode: value})}
            hint={t('page.datagrid.settings.emptyCellsHint', {
              defaultValue: 'What shows in cells that have no value: nothing, or a dash (–).',
            })}
          />
          <SegmentedControl
            label={t('page.datagrid.settings.headerWrapping', {defaultValue: 'Header wrapping'})}
            options={translateOptions(WRAP_MODE_OPTIONS, t)}
            value={props.settings.headerWrapMode ?? 'TRUNCATE'}
            onChange={(value) => props.onSettingsChange({headerWrapMode: value})}
            hint={t('page.datagrid.settings.headerWrappingHint', {
              defaultValue:
                'Wrap shows full header text on multiple lines; truncate shows ellipsis.',
            })}
          />
        </SettingGroup>

        <SettingGroup title={t('page.datagrid.settings.gridLines', {defaultValue: 'Grid lines'})}>
          <SettingToggle
            label={t('page.datagrid.settings.zebraStriping', {defaultValue: 'Zebra striping'})}
            hint={t('page.datagrid.settings.zebraStripingHint', {
              defaultValue: 'Shade alternating rows for easier scanning.',
            })}
            value={props.settings.stripedRows}
            onChange={(value) => props.onSettingsChange({stripedRows: value})}
            isFirst
          />
          <SettingToggle
            label={t('page.datagrid.settings.horizontalSeparators', {
              defaultValue: 'Horizontal separators',
            })}
            hint={t('page.datagrid.settings.horizontalSeparatorsHint', {
              defaultValue: '1px rule between rows.',
            })}
            value={props.settings.horizontalSeparatorsVisible}
            onChange={(value) => props.onSettingsChange({horizontalSeparatorsVisible: value})}
          />
          <SettingToggle
            label={t('page.datagrid.settings.verticalSeparators', {
              defaultValue: 'Vertical separators',
            })}
            hint={t('page.datagrid.settings.verticalSeparatorsHint', {
              defaultValue: '1px rule between columns.',
            })}
            value={props.settings.verticalSeparatorsVisible}
            onChange={(value) => props.onSettingsChange({verticalSeparatorsVisible: value})}
          />
        </SettingGroup>

        <SettingGroup
          title={t('page.datagrid.settings.behaviour', {defaultValue: 'Behaviour'})}
          isLast
        >
          <Show
            when={
              props.presetDisplayMode != null && props.onPresetDisplayModeChange
                ? {
                    mode: props.presetDisplayMode,
                    onChange: props.onPresetDisplayModeChange,
                  }
                : null
            }
          >
            {(preset) => (
              <SegmentedControl
                label={t('page.datagrid.settings.presetDisplay', {
                  defaultValue: 'Preset display',
                })}
                options={translateOptions(PRESET_DISPLAY_MODE_OPTIONS, t)}
                value={preset.mode}
                onChange={preset.onChange}
                hint={t('page.datagrid.settings.presetDisplayHint', {
                  defaultValue:
                    'Choose how saved views appear: tab strip, scrollable strip, dropdown, or hidden.',
                })}
              />
            )}
          </Show>
          <SegmentedControl
            label={t('page.datagrid.labels.rowsPerPage', {defaultValue: 'Rows per page'})}
            options={PAGE_SIZE_OPTIONS}
            value={props.settings.itemsPerPage}
            onChange={(value) => props.onSettingsChange({itemsPerPage: value})}
          />
          {/* Experimental — hidden behind a Flagsmith flag until the input variant is validated. */}
          <Show whenFeatureEnabled={PAGINATION_DISPLAY_MODE_FEATURE_FLAG}>
            <SegmentedControl
              label={t('page.datagrid.settings.paginationDisplay', {
                defaultValue: 'Pagination style',
              })}
              options={translateOptions(PAGINATION_DISPLAY_MODE_OPTIONS, t)}
              value={props.settings.paginationDisplayMode ?? DEFAULT_PAGINATION_DISPLAY_MODE}
              onChange={(value) => props.onSettingsChange({paginationDisplayMode: value})}
              hint={t('page.datagrid.settings.paginationDisplayHint', {
                defaultValue:
                  'How the footer navigates pages: numbered page buttons, or a page-number input.',
              })}
            />
          </Show>
          <SettingToggle
            label={t('page.datagrid.settings.columnGroups', {defaultValue: 'Column groups'})}
            hint={t('page.datagrid.settings.columnGroupsHint', {
              defaultValue: 'Show the grouped header band above columns.',
            })}
            value={props.settings.columnGroupsVisible}
            onChange={(value) => props.onSettingsChange({columnGroupsVisible: value})}
          />
          <SettingToggle
            label={t('page.datagrid.settings.filteredByBar', {defaultValue: 'Filtered by bar'})}
            hint={t('page.datagrid.settings.filteredByBarHint', {
              defaultValue: 'Show the bar of active filter and sort chips above the table.',
            })}
            value={props.settings.activeFilterBarVisible ?? true}
            onChange={(value) => props.onSettingsChange({activeFilterBarVisible: value})}
          />
          <Show when={props.columnSortMode === 'MULTI_SORT'}>
            <SettingToggle
              label={t('page.datagrid.settings.confirmSortReplacement', {
                defaultValue: 'Confirm replacing multiple sorts',
              })}
              hint={t('page.datagrid.settings.confirmSortReplacementHint', {
                defaultValue:
                  'Ask before a regular header click replaces multiple sorts. Shift-click keeps them.',
              })}
              value={props.settings.confirmSortReplacement ?? true}
              onChange={(value) => props.onSettingsChange({confirmSortReplacement: value})}
            />
          </Show>
          <SettingToggle
            label={t('page.datagrid.settings.summaryRow', {defaultValue: 'Summary row'})}
            hint={t('page.datagrid.settings.summaryRowHint', {
              defaultValue: 'Show a summary row with aggregated values at the bottom of the grid.',
            })}
            value={props.summaryRowEnabled ? props.settings.summaryRowVisible : false}
            onChange={(value) => props.onSettingsChange({summaryRowVisible: value})}
            isDisabled={!props.summaryRowEnabled}
            disabledTooltip={t('page.datagrid.settings.summaryRowDisabledTooltip', {
              defaultValue: 'Summary row is not enabled for this data grid.',
            })}
          />
        </SettingGroup>
      </div>
    </div>
  );
}
